import {
  DASHBOARD_SEARCH_KEY_PREFIX,
  DASHBOARD_VIEW_KEY_PREFIX,
  searchKey,
  viewKey,
} from "./constants";

/** Current schema version for a persisted dashboard view. Bump on a breaking shape change. */
const CURRENT_VIEW_VERSION = 1;

/** The payload held under one dashboard-view storage key. */
export interface StoredView {
  v: number;
  /** Serialised values only, keyed by the schema field's query-parameter name. */
  values: Record<string, string>;
}

/**
 * All storage access goes through these two wrappers. Private browsing, a
 * locked-down browser policy or a full quota can all throw on read or write;
 * callers must never surface that, and must never log it (it would risk
 * logging view/search content alongside the error).
 */
const readRaw = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (storage: Storage, key: string, value: string): void => {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage unavailable or full: degrade to address-bar-only persistence.
  }
};

const removeRaw = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key);
  } catch {
    // No-op: nothing to clean up if storage itself is unreachable.
  }
};

const keysStartingWith = (storage: Storage, prefix: string): string[] => {
  try {
    return Object.keys(storage).filter((key) => key.startsWith(prefix));
  } catch {
    return [];
  }
};

const parseStoredView = (raw: string | null): StoredView | undefined => {
  if (raw === null) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CURRENT_VIEW_VERSION || typeof parsed.values !== "object") {
      return undefined;
    }
    return parsed as StoredView;
  } catch {
    return undefined;
  }
};

const readStoredView = (storage: Storage, key: string): Record<string, string> =>
  parseStoredView(readRaw(storage, key))?.values ?? {};

const writeStoredView = (
  storage: Storage,
  key: string,
  values: Record<string, string>
): void => {
  if (Object.keys(values).length === 0) {
    removeRaw(storage, key);
    return;
  }
  writeRaw(storage, key, JSON.stringify({ v: CURRENT_VIEW_VERSION, values }));
};

/** Reads the non-session portion of a dashboard's stored view (localStorage). */
export const readView = (
  dashboard: string,
  tenantId: number | undefined,
  userId: number
): Record<string, string> =>
  readStoredView(window.localStorage, viewKey(dashboard, tenantId, userId));

/** Writes the non-session portion of a dashboard's view. Empty values removes the entry. */
export const writeView = (
  dashboard: string,
  tenantId: number | undefined,
  userId: number,
  values: Record<string, string>
): void =>
  writeStoredView(window.localStorage, viewKey(dashboard, tenantId, userId), values);

export const removeView = (
  dashboard: string,
  tenantId: number | undefined,
  userId: number
): void => removeRaw(window.localStorage, viewKey(dashboard, tenantId, userId));

/** Reads the session-scoped portion of a dashboard's view (sessionStorage; e.g. search term). */
export const readSessionView = (
  dashboard: string,
  tenantId: number | undefined,
  userId: number
): Record<string, string> =>
  readStoredView(window.sessionStorage, searchKey(dashboard, tenantId, userId));

export const writeSessionView = (
  dashboard: string,
  tenantId: number | undefined,
  userId: number,
  values: Record<string, string>
): void =>
  writeStoredView(window.sessionStorage, searchKey(dashboard, tenantId, userId), values);

export const removeSessionView = (
  dashboard: string,
  tenantId: number | undefined,
  userId: number
): void => removeRaw(window.sessionStorage, searchKey(dashboard, tenantId, userId));

/**
 * Removes every persisted dashboard view and search entry, across all
 * dashboards, tenants and users. Called on sign-out (wired up in DFP-S008).
 * Never throws.
 */
export const clearAllDashboardViews = (): void => {
  keysStartingWith(window.localStorage, DASHBOARD_VIEW_KEY_PREFIX).forEach((key) =>
    removeRaw(window.localStorage, key)
  );
  keysStartingWith(window.sessionStorage, DASHBOARD_SEARCH_KEY_PREFIX).forEach((key) =>
    removeRaw(window.sessionStorage, key)
  );
};
